/**
 * ErrorBlock — Graceful error display with retry and suggestions
 *
 * Shows an error icon + message, optional retry button, and
 * alternative suggestions if available. Matches dark theme.
 */
import React from 'react';
import type { ErrorBlockData } from '../types';
import { AlertCircle, RefreshCw } from '../../shared';

// =============================================================================
// COMPONENT
// =============================================================================

interface ErrorBlockProps {
  data: ErrorBlockData;
  onAction?: (prompt: string) => void;
}

export const ErrorBlock: React.FC<ErrorBlockProps> = ({ data, onAction }) => {
  const { message, retryable, suggestions } = data;

  const handleRetry = () => {
    if (onAction) {
      onAction('__retry__');
    }
  };

  return (
    <div className="rounded-2xl bg-red-400/[0.04] border border-red-400/20 px-5 py-4 space-y-3">
      {/* Error header */}
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-400/80 shrink-0 mt-0.5" />
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-red-300/90 leading-relaxed">
            {message}
          </p>
        </div>
      </div>

      {/* Retry button */}
      {retryable && (
        <button
          onClick={handleRetry}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-cyan-400/80 bg-cyan-400/10 border border-cyan-400/25 hover:bg-cyan-400/20 hover:text-cyan-300 hover:border-cyan-400/40 transition-all duration-200 active:scale-95"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Try again
        </button>
      )}

      {/* Alternative suggestions */}
      {suggestions && suggestions.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400/30">
            Try instead
          </span>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, i) => (
              <button
                key={i}
                onClick={() => onAction?.(suggestion)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-cyan-400/60 bg-cyan-400/[0.04] border border-cyan-400/15 hover:bg-cyan-400/10 hover:text-cyan-300 hover:border-cyan-400/30 transition-all duration-200 active:scale-[0.97]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorBlock;
